import {UrlUtility} from "./UrlUtility";
import {Role} from "../../types/Role";

export interface ApiRequestOptions {
    method?: string;
    headers: HeadersInit;
    body?: string;
}

export interface LoginResponse {
    user: object;
    permissions: object[];
}

export interface ListFetchResponse {
    list: object[]
    pageCount?: number
}

export let ApiUtility = {
    login: function (username: string, password: string): Promise<LoginResponse> {
        return new Promise(function (resolve, reject) {
            fetch(
                '/api/users/login',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        username: username,
                        password: password
                    })
                }
            )
                .then(function (rawResponse) {
                    if (rawResponse.status === 401 || rawResponse.status === 404 || rawResponse.status === 500) {
                        return rawResponse.json().then(function (body: any) {
                            throw body.message || rawResponse.statusText;
                        });
                    } else {
                        return rawResponse.json();
                    }
                })
                .then(function (responseData) {
                    resolve(responseData);
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    },

    requestLoginOtp: function (email: string): Promise<void> {
        return new Promise(function (resolve, reject) {
            fetch(
                '/api/users/request-login-otp',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        email: email
                    })
                }
            )
                .then(function (rawResponse) {
                    if (rawResponse.status === 429) {
                        return rawResponse.json().then(function (body: any) {
                            throw body.error || rawResponse.statusText;
                        });
                    } else {
                        resolve();
                    }
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    },

    verifyLoginOtp: function (email: string, code: string): Promise<LoginResponse> {
        return new Promise(function (resolve, reject) {
            fetch(
                '/api/users/verify-login-otp',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        email: email,
                        code: code
                    })
                }
            )
                .then(function (rawResponse) {
                    if (rawResponse.status === 429) {
                        return rawResponse.json().then(function (body: any) {
                            throw body.error || rawResponse.statusText;
                        });
                    } else {
                        return rawResponse.json();
                    }
                })
                .then(function (responseData) {
                    resolve(responseData);
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    },

    apiRequest: function (url: string, options?: ApiRequestOptions, overrideToken?: string): Promise<any> {
        // fetch from API
        let extendedHeaders: any = (options && options.headers) || {};
        if (overrideToken) {
            extendedHeaders['wwwauthenticate'] = overrideToken;
        }
        return new Promise(function (resolve, reject) {
            fetch(
                url,
                {
                    ...(options || {}),
                    headers: extendedHeaders
                }
            )
                .then(function (rawResponse) {
                    if (rawResponse.status === 401) {
                        window.location.assign('/login');
                    }
                    if (rawResponse.status === 401 || rawResponse.status === 403 || rawResponse.status === 404 || rawResponse.status === 422 || rawResponse.status === 500) {
                        return rawResponse.json().then(function (body: any) {
                            throw new Error(body.message || rawResponse.statusText);
                        });
                    } else {
                        return rawResponse.json();
                    }
                })
                .then(function (responseData) {
                    resolve(responseData);
                })
                .catch(function (error) {
                    reject(error);
                });
        });
    },

    logout: function () {
        return ApiUtility.apiRequest(`/api/users/logout`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            }
        })
    },

    sendPasswordResetEmail: function (data: object) {
        return ApiUtility.apiRequest(
            '/api/users/send-password-reset-email',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            },
        )
    },

    resendActivationEmail: function (data: object) {
        return ApiUtility.apiRequest(
            '/api/users/resend-activation-email',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            },
        )
    },

    changePassword: function (data: object, token: string) {
        return ApiUtility.apiRequest(
            '/api/users/change-password/',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            },
            decodeURIComponent(token)
        )
    },

    setUpNewUser: function (data: object, token: string) {
        return ApiUtility.apiRequest(
            '/api/users/set-up-new-user/',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            },
            decodeURIComponent(token)
        )
    },

    validatePasswordResetToken: function (token: string) {
        return ApiUtility.apiRequest(`/api/users/validate-password-reset-token/${encodeURIComponent(token)}`);
    },

    validateNewAccountToken: function (token: string) {
        return ApiUtility.apiRequest(`/api/users/validate-new-account-token/${encodeURIComponent(token)}`);
    },

    getIsPersonEmailAvailable: function (email: string) {
        return ApiUtility.apiRequest(`/api/users/is-person-email-available/${encodeURIComponent(email)}`);
    },

    getBrokersList: function (sortDirection?: string): Promise<ListFetchResponse> {
        let argumentsArray = [];

        if (sortDirection) {
            argumentsArray.push({label: 'sortDirection', value: sortDirection})
        }

        return ApiUtility.apiRequest(`/api/brokers${UrlUtility.getUrlQueryString(argumentsArray)}`);
    },

    getBroker: function (brokerId: number) {
        return ApiUtility.apiRequest(`/api/brokers/${brokerId}`);
    },

    createBroker: function (brokerData: object) {
        return ApiUtility.apiRequest(
            '/api/brokers/',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(brokerData),
            }
        );
    },

    updateBroker: function (brokerData: { id: string }) {
        return ApiUtility.apiRequest(
            `/api/brokers/${brokerData.id}`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(brokerData)
            }
        );
    },

    getUsersList: function (page?: number, sortDirection?: string, role?: string, searchText?: string, showInactive?: boolean): Promise<ListFetchResponse> {
        let argumentsArray = [];

        if (sortDirection) {
            argumentsArray.push({label: 'sortDirection', value: sortDirection})
        }

        if (page) {
            argumentsArray.push({label: 'page', value: page})
        }

        if (role) {
            argumentsArray.push({label: 'role', value: role})
        }

        if (searchText) {
            argumentsArray.push({label: 'searchText', value: searchText})
        }

        if (showInactive) {
            argumentsArray.push({label: 'showInactive', value: showInactive})
        }

        return ApiUtility.apiRequest(`/api/users${UrlUtility.getUrlQueryString(argumentsArray)}`);
    },

    getUser: function (userId: number) {
        return ApiUtility.apiRequest(`/api/users/${userId}`);
    },

    createUser: function (userData: object) {
        return ApiUtility.apiRequest(
            '/api/users/',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(userData),
            }
        );
    },

    updateUser: function (userData: { id: string }) {
        return ApiUtility.apiRequest(
            `/api/users/${userData.id}`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(userData)
            }
        );
    },

    getJobsList: function (page?: number, sortDirection?: string, searchText?: string): Promise<ListFetchResponse> {
        let argumentsArray = [];

        if (page) {
            argumentsArray.push({label: 'page', value: page})
        }

        if (sortDirection) {
            argumentsArray.push({label: 'sortDirection', value: sortDirection})
        }

        if (searchText) {
            argumentsArray.push({label: 'searchText', value: searchText})
        }

        return ApiUtility.apiRequest(`/api/jobs${UrlUtility.getUrlQueryString(argumentsArray)}`);
    },

    getJob: function (jobId: number | string) {
        return ApiUtility.apiRequest(`/api/jobs/${jobId}`);
    },

    getRolesList: function (): Promise<Role[]> {
        return ApiUtility.apiRequest(`/api/roles`);
    },

    getDispatchOrdersListForDate: function (
        dateString: string,
        searchText: any,
        statusId: any,
        jobId: any,
        brokerId: any,
        foremanUserId: any
    ): Promise<any> {
        let argumentsArray = [];

        if (searchText) {
            argumentsArray.push({label: 'searchText', value: searchText})
        }

        if (statusId) {
            argumentsArray.push({label: 'statusId', value: statusId})
        }

        if (jobId) {
            argumentsArray.push({label: 'jobId', value: jobId})
        }

        if (brokerId) {
            argumentsArray.push({label: 'brokerId', value: brokerId})
        }

        if (foremanUserId) {
            argumentsArray.push({label: 'foremanUserId', value: foremanUserId})
        }

        return ApiUtility.apiRequest(`/api/dispatch-orders/for-date/${dateString}${UrlUtility.getUrlQueryString(argumentsArray)}`);
    },


    getDispatchOrder: function (dispatchOrderId: number) {
        return ApiUtility.apiRequest(`/api/dispatch-orders/${dispatchOrderId}`);
    },

    createDispatchOrder: function (dispatchOrderData: object) {
        return ApiUtility.apiRequest(
            '/api/dispatch-orders/',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dispatchOrderData),
            }
        );
    },

    updateDispatchOrder: function (dispatchOrderData: any) {
        return ApiUtility.apiRequest(
            `/api/dispatch-orders/${dispatchOrderData.id}`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dispatchOrderData)
            }
        );
    },

    updateDispatchOrderStatus: function (dispatchOrderStatusUpdateData: any) {
        return ApiUtility.apiRequest(
            `/api/dispatch-orders/${dispatchOrderStatusUpdateData.id}/update-status`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dispatchOrderStatusUpdateData)
            }
        );
    },

    sendDispatchOrderNotificationWithoutUpdate: function (dispatchOrderId: string, notification: any) {
        return ApiUtility.apiRequest(
            `/api/dispatch-orders/${dispatchOrderId}/send-notification-without-update`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({notification: notification}),
            }
        );
    },

    getDispatchOrderStatuses: function () {
        return ApiUtility.apiRequest(`/api/dispatch-order-statuses`);
    },

    getDispatchOrderCancellationReasons: function () {
        return ApiUtility.apiRequest(`/api/dispatch-order-cancellation-reasons`);
    },

    getBrokersForDropdown: function () {
        return ApiUtility.apiRequest(`/api/brokers/for-dropdown`);
    },

    getDashboardBrokerUsageForDate: function (dateString: string) {
        return ApiUtility.apiRequest(`/api/dashboard/broker-usage/for-date/${dateString}`);
    },

    getDashboardJobOrderCountsForDate: function (dateString: string) {
        return ApiUtility.apiRequest(`/api/dashboard/job-order-counts/for-date/${dateString}`);
    },

    getActivityMaterials: function (activeOnly?: boolean) {
        let argumentsArray = [];

        if (activeOnly) {
            argumentsArray.push({label: 'activeOnly', value: activeOnly})
        }

        return ApiUtility.apiRequest(`/api/activity-materials${UrlUtility.getUrlQueryString(argumentsArray)}`);
    },

    createActivityMaterial: function (activityMaterialData: object) {
        return ApiUtility.apiRequest(
            '/api/activity-materials/',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(activityMaterialData),
            }
        );
    },

    updateActivityMaterial: function (activityMaterialData: { id: string }) {
        return ApiUtility.apiRequest(
            `/api/activity-materials/${activityMaterialData.id}`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(activityMaterialData)
            }
        );
    },

    getJobsForDropdown: function () {
        return ApiUtility.apiRequest(`/api/jobs/for-dropdown`);
    },

    getForemenForDropdown: function () {
        return ApiUtility.apiRequest(`/api/users/foremen-for-dropdown`);
    },

    getActivityDirections: function () {
        return ApiUtility.apiRequest(`/api/activity-directions`);
    },

    getActivityScopes: function (activeOnly?: boolean) {
        let argumentsArray = [];

        if (activeOnly) {
            argumentsArray.push({label: 'activeOnly', value: activeOnly})
        }

        return ApiUtility.apiRequest(`/api/activity-scopes${UrlUtility.getUrlQueryString(argumentsArray)}`);
    },

    createActivityScope: function (activityScopeData: object) {
        return ApiUtility.apiRequest(
            '/api/activity-scopes/',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(activityScopeData),
            }
        );
    },

    updateActivityScope: function (activityScopeData: { id: string }) {
        return ApiUtility.apiRequest(
            `/api/activity-scopes/${activityScopeData.id}`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(activityScopeData)
            }
        );
    },

    getBrokerAssignmentCountForDate: function (brokerId: number, dateString: string, dispatchOrderId: number) {
        return ApiUtility.apiRequest(`/api/brokers/${brokerId}/assignment-info-for-date/${dateString}/${dispatchOrderId}`);
    },

    getBrokerReportsListForDate: function (dateString: string, brokerId: any, statusId: any) {
        let argumentsArray = [];

        if (brokerId) {
            argumentsArray.push({label: 'brokerId', value: brokerId})
        }

        if (statusId) {
            argumentsArray.push({label: 'statusId', value: statusId})
        }

        return ApiUtility.apiRequest(`/api/broker-reports/for-date/${dateString}${UrlUtility.getUrlQueryString(argumentsArray)}`);
    },

    getBrokerReportForDate: function (brokerId: number | string, dateString: string) {
        return ApiUtility.apiRequest(`/api/broker-reports/${brokerId}/for-date/${dateString}`);
    },

    getPublicBrokerPlan: function (token: string) {
        return ApiUtility.apiRequest(`/api/broker-reports/plans/${token}`);
    },

    createBrokerReportLink: function (brokerId: number | string, dateString: string) {
        return ApiUtility.apiRequest(
            `/api/broker-reports/${brokerId}/for-date/${dateString}/link`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );
    },

    shareBrokerReportByText: function (brokerId: number | string, dateString: string, shareData: object) {
        return ApiUtility.apiRequest(
            `/api/broker-reports/${brokerId}/for-date/${dateString}/share-by-text`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(shareData),
            }
        );
    },

    shareBrokerReportByEmail: function (brokerId: number | string, dateString: string, shareData: object) {
        return ApiUtility.apiRequest(
            `/api/broker-reports/${brokerId}/for-date/${dateString}/share-by-email`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(shareData),
            }
        );
    },

    recordBrokerReportPrint: function (brokerId: number | string, dateString: string, printData: object) {
        return ApiUtility.apiRequest(
            `/api/broker-reports/${brokerId}/for-date/${dateString}/record-print`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(printData),
            }
        );
    },

    deleteNoActivityReport: function (noActivityReportId: number) {
        return ApiUtility.apiRequest(
            `/api/no-activity-reports/${noActivityReportId}`,
            {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );
    },

    syncUsers: function () {
        return ApiUtility.apiRequest(`/api/users/sync`);
    },

    syncJobs: function () {
        return ApiUtility.apiRequest(`/api/jobs/sync`);
    },
};
